// services/legrandServices.ts
import axios from 'axios';
import { API_CONFIG, getAuthHeader } from '../api/config';
import { Demandes } from '../models/Demandes';
import { Voyageurs } from '../models/Voyageurs';
import { PieceJointe } from '../models/PieceJointe';
import { COMPANIES } from '../config/companies';
import { creerApiFormulaires } from '../formulaires/api';
import type { ApiFormulaires } from '../formulaires/api';

const BASE_URL = `${API_CONFIG.baseUrl}/api/legrand`;

// Fonction pour obtenir les headers avec authentification
const getHeaders = () => ({
  ...getAuthHeader('legrand'),
  'Content-Type': 'application/json'
});

// ========== DEMANDES ==========

export const recupereListeDemandesLegrand = async (includeArchived: boolean = false): Promise<Demandes[]> => {
  try {
    const response = await axios.get<Demandes[]>(`${BASE_URL}/demandes`, {
      headers: getAuthHeader('legrand'),
      params: { includeArchived }
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la r\u00e9cup\u00e9ration des demandes Legrand:', error);
    throw error;
  }
};

export const recupereDemandeById = async (id: number): Promise<Demandes> => {
  try {
    const response = await axios.get(`${BASE_URL}/demandes/${id}`, {
      headers: getAuthHeader('legrand')
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la r\u00e9cup\u00e9ration de la demande:', error);
    throw error;
  }
};

// ========== COMMENTAIRES ==========

export const majComLegrand = async (chemin: string, commentaires: string) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/majdemande/${chemin}/commentaires`,
      { commentaires },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise \u00e0 jour des commentaires:', error);
    throw error;
  }
};

export const majComVisaLegrand = async (chemin: string, commentairesVisa: string) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/majdemande/${chemin}/commentairesvisa`,
      { commentairesVisa },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise \u00e0 jour des commentaires visa:', error);
    throw error;
  }
};

export const supprimerComVisaEtPasserATraiterLegrand = async (chemin: string) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/majdemande/${chemin}/commentairesvisa-et-traiter`,
      { headers: getAuthHeader('legrand') }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la suppression des commentaires visa:', error);
    throw error;
  }
};

// ========== ASSURANCE ==========

export const traitementAssuranceLegrand = async (demandeId: number, newAssuranceValue: string) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/demandes/${demandeId}/traitement-assurance`,
      { newAssuranceValue },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors du traitement de l'assurance:", error);
    throw error;
  }
};

// ========== VOYAGEURS ==========

export const recupererInfoVoyageurLegrand = async (idVoyageur: number): Promise<Voyageurs> => {
  try {
    const response = await axios.get(`${BASE_URL}/voyageurs/${idVoyageur}`, {
      headers: getAuthHeader('legrand')
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la r\u00e9cup\u00e9ration du voyageur:', error);
    throw error;
  }
};

export const modifierInfoVoyageurLegrand = async (voyageur: Voyageurs): Promise<void> => {
  try {
    await axios.put(
      `${BASE_URL}/voyageurs/${voyageur.id}`,
      voyageur,
      { headers: getHeaders() }
    );
  } catch (error) {
    console.error('Erreur lors de la modification du voyageur:', error);
    throw error;
  }
};

// ========== UTILISATEURS ==========

export const recupereInfosClientLegrand = async (email: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/utilisateur/${email}`, {
      headers: getAuthHeader('legrand')
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la r\u00e9cup\u00e9ration des informations du client:', error);
    throw error;
  }
};

// ========== STATISTIQUES ==========

export const recupereStatsLegrand = async (visaFilter?: 'all' | 'visa' | 'evisa') => {
  try {
    const params: any = {};
    if (visaFilter && visaFilter !== 'all') {
      params.filter = visaFilter;
    }

    const response = await axios.get(`${BASE_URL}/statistiques`, {
      headers: getAuthHeader('legrand'),
      params
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la r\u00e9cup\u00e9ration des statistiques:', error);
    throw error;
  }
};

// ========== BONS DE COMMANDE ==========

export const recupereBDCLegrand = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/bons-commande`, {
      headers: getAuthHeader('legrand')
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la r\u00e9cup\u00e9ration des bons de commande:', error);
    throw error;
  }
};

// ========== AFFILIATIONS ==========

export const getAffiliationAdresses = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/affiliation/adresses`, {
      headers: getAuthHeader('legrand')
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des adresses d\'affiliation:', error);
    throw error;
  }
};

export const setAffiliationAdresse = async (demandeId: number, affiliationId: number | null) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/demandes/${demandeId}/affiliation`,
      { affiliationId },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'affiliation:', error);
    throw error;
  }
};

// ========== UPLOADS (utilise le backend principal avec filtrage) ==========

export const uploadPasseportLegrand = async (dossier: string, formData: FormData): Promise<any> => {
  const response = await fetch(`${API_CONFIG.baseUrl}/voyageurs/${dossier}/uploadPasseport`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Erreur lors de l'upload du passeport");
  }

  return response.json();
};

export const uploadPhotoVoyageurLegrand = async (idVoyageur: number, formData: FormData): Promise<any> => {
  const response = await fetch(`${API_CONFIG.baseUrl}/voyageurs/${idVoyageur}/uploadPhoto`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Erreur lors de l'upload de la photo");
  }

  return response.json();
};

export const uploadVisaLegrand = async (dossier: string, formData: FormData) => {
  const url = `${API_CONFIG.baseUrl}/voyageurs/${dossier}/uploadVisa`;
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Erreur lors de l'upload du visa");
  }

  return await response.json();
};

export const uploadEcardLegrand = async (dossier: string, formData: FormData) => {
  const url = `${API_CONFIG.baseUrl}/voyageurs/${dossier}/uploadEcard`;
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Erreur lors de l'upload de l'e-card");
  }

  return await response.json();
};

// ========== AUTRES FONCTIONS ==========

export const recupererPasseportLegrand = async (cheminVoyageur: string): Promise<string> => {
  try {
    const response = await axios.get(`${API_CONFIG.baseUrl}/voyageurs/passeport`, {
      params: { chemin: cheminVoyageur }
    });
    return response.data.urlPasseport;
  } catch (error) {
    console.error("Erreur lors de la r\u00e9cup\u00e9ration de l'URL du passeport", error);
    throw error;
  }
};

// Pi\u00e8ces jointes du dossier (m\u00eame liste que l'admin) : [{ nom, url, supprimable }]
export const listerPiecesJointesLegrand = async (chemin: string): Promise<PieceJointe[]> =>
  (await axios.get<PieceJointe[]>(`${API_CONFIG.baseUrl}/piecesJointes/${encodeURIComponent(chemin)}/fichiers`)).data;

export const recupererContenuFormulaireLegrand = async (chemin: string) => {
  try {
    const response = await axios.get(`${API_CONFIG.baseUrl}/formulaire/${chemin}`);
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la r\u00e9cup\u00e9ration du contenu du formulaire", error);
    throw error;
  }
};

// R\u00e9cup\u00e8re l'URL de l'e-card si le PDF a \u00e9t\u00e9 upload\u00e9 (renvoie null sinon)
export const recupererEcardLegrand = async (chemin: string): Promise<string | null> => {
  try {
    const response = await axios.get(`${API_CONFIG.baseUrl}/demandes/${chemin}/ecard`);
    return response.data.urlEcard || null;
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    console.error("Erreur lors de la r\u00e9cup\u00e9ration de l'URL de l'e-card", error);
    return null;
  }
};

export const majDemandeLegrand = async (chemin: string, data: {
  datevalidite: string,
  dateexpiration: string,
  infosvisa: string
}) => {
  try {
    const response = await axios.put(`${API_CONFIG.baseUrl}/majdemande/${chemin}`, data);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise \u00e0 jour de la demande:', error);
    throw error;
  }
};

export async function envoyerMailVisaTraiteLegrand(chemin: string, data: {
  pays: string;
  datevalidite: string;
  dateexpiration: string;
  infosvisa: string;
  subjectMail: string;
  mailContent: string;
  sendEmail?: string;
}) {
  await axios.post(`${API_CONFIG.baseUrl}/voyageurs/${chemin}/visaTraite`, data);
}

export const marquerFactureRecuperee = async (demandeId: number) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/demandes/${demandeId}/facture/recuperee`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors du marquage de la facture comme récupérée:', error);
    throw error;
  }
};

export const archiverDemande = async (id: number) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/demandes/${id}/archiver`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de l\'archivage de la demande:', error);
    throw error;
  }
};

export const desarchiverDemande = async (id: number) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/demandes/${id}/desarchiver`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors du désarchivage de la demande:', error);
    throw error;
  }
};

// Upload d'un document générique dans le dossier de la demande Legrand
export const uploadDocumentDemandeLegrand = async (chemin: string, formData: FormData): Promise<any> => {
  const response = await fetch(`${BASE_URL}/demandes/${chemin}/uploadDocument`, {
    method: 'POST',
    body: formData,
    headers: {
      ...getAuthHeader('legrand')
    }
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'upload du document');
  }

  return response.json();
};

// Suppression d'un document déposé par erreur (rangé dans supprimes/ sur le serveur) ; l'erreur porte le message du backend
export const supprimerPieceJointeLegrand = async (chemin: string, nom: string): Promise<void> => {
  const response = await fetch(
    `${BASE_URL}/demandes/${encodeURIComponent(chemin)}/piecesJointes/${encodeURIComponent(nom)}`,
    { method: 'DELETE', headers: { ...getAuthHeader('legrand') } }
  );
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error((data && data.message) || 'Erreur lors de la suppression du document.');
  }
};

// ========== NOUVEAU DOSSIER ET FORMULAIRES JOINTS (superadmin) ==========
// Même « Nouveau dossier » et mêmes formulaires que l'admin (composants partagés NouveauDossierDialog / FormulairesDossier).
// Backend routes/legrandFormulaires.js : dossiers @legrand.com / @legrand.fr seulement, écriture réservée au superadmin,
// jeton du lien client masqué pour les autres rôles.

// Un dossier dont le compte n'est pas Legrand n'apparaîtrait pas dans cette liste (le backend le refuse aussi)
export const erreurEmailLegrand = (email: string): string | null => {
  const e = email.trim().toLowerCase();
  return COMPANIES.legrand.emailDomains.some((d) => e.endsWith(`@${d}`))
    ? null
    : `Adresse ${COMPANIES.legrand.emailDomains.map((d) => `@${d}`).join(' ou ')} obligatoire`;
};

export const creerDossierLegrand = async (data: {
  email: string;
  pays: string;
  validite: string;
  dateDemande: string;
  nom: string;
  prenom: string;
  dateDepart: string;
  etatVisa: string;
}): Promise<{ id: number; chemin: string; id_voyageur: number; id_utilisateur: number }> => {
  const response = await axios.post(`${BASE_URL}/demandes`, data, { headers: getHeaders() });
  return response.data;
};

// Token relu à chaque appel (creerApiFormulaires)
export const apiFormulairesLegrand: ApiFormulaires = creerApiFormulaires(`${BASE_URL}/formulaires`, () => getAuthHeader('legrand'));

// ========== ALERTES PLANIFIÉES ==========
// Table et planificateur d'envoi mutualisés avec l'admin standard : ces routes
// n'exposent que les dossiers du périmètre Legrand.

export interface AlerteDossier {
  id: number;
  id_demande: number;
  datetime_envoi: string;
  message: string;
  statut: 'en_attente' | 'envoye' | 'echec';
  /** null = alerte manuelle ; non null = marqueur posé par un cron (ex. 'relance_ecard_j4') */
  type?: string | null;
  date_creation?: string;
  date_envoi?: string | null;
}

export const recupererAlertesLegrand = async (demandeId: number): Promise<AlerteDossier[]> => {
  try {
    const response = await axios.get<AlerteDossier[]>(`${BASE_URL}/demandes/${demandeId}/alertes`, {
      headers: getAuthHeader('legrand'),
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des alertes :', error);
    return [];
  }
};

export const creerAlerteLegrand = async (
  demandeId: number,
  datetimeEnvoi: string,
  message: string
): Promise<AlerteDossier> => {
  const response = await axios.post<AlerteDossier>(
    `${BASE_URL}/demandes/${demandeId}/alertes`,
    { datetime_envoi: datetimeEnvoi, message },
    { headers: getHeaders() }
  );
  return response.data;
};

export const supprimerAlerteLegrand = async (alerteId: number): Promise<void> => {
  await axios.delete(`${BASE_URL}/alertes/${alerteId}`, {
    headers: getAuthHeader('legrand'),
  });
};